// The station sheet's lockdown sections (2026-10-01):
//
//   * Lockdown test (F-STN-21, spec/13 §12) — asks the PC to check its own lockdown (`station.lockdown.test`) and shows
//     the answer per check: the failing ones first, named, then the rest. "Run it on one PC before the rest" — the
//     onboarding step. It is the station's self-check; the escape attempts themselves are the VM suite's.
//   * Recovery code (F-LCK-06, spec/11 §4.3) — the one-time code an administrator on a broken PC needs for
//     `station-cli undo`. Owner and manager only (`stations:recovery`); shown once with the command to paste, never kept.
//
// Both reuse the drawer's existing styling (the health list and the offline-code display): no new classes.

import type { StationLockdownTest, StationRecoveryCode } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, cx } from '@clavian/ui-web';
import { useEffect, useState, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../api/problems';
import { useProblem } from '../api/use-problem';
import { useCurrentVenue } from '../shell/scope';
import { Icon } from '../shell/nav-icons';
import { Notice, Section } from '../ui';
import { useIssueRecoveryCode, useRunLockdownTest, useStationLockdownTest } from './queries';

/**
 * The command an administrator runs on the PC (spec/10 §12), for PowerShell — Windows' default terminal. A quoted path
 * alone is only a string there; the call operator `&` runs it (owner, 2026-10-01: "Unexpected token 'undo'").
 */
export function undoCommand(code: string): string {
  return `& "C:\\Program Files\\Clavian\\Station\\Clavian.Station.Service.exe" undo --code ${code}`;
}

/** The failing checks first, then the rest, each group in the station's order. */
export function orderedChecks(test: StationLockdownTest): StationLockdownTest['checks'] {
  return [...test.checks.filter((check) => check.outcome === 'fail'), ...test.checks.filter((check) => check.outcome !== 'fail')];
}

export function LockdownTestSection({ stationId, canRun }: { readonly stationId: string; readonly canRun: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const venue = useCurrentVenue();
  const test = useStationLockdownTest(venue.organizationId, venue.id, stationId);
  const run = useRunLockdownTest(venue.organizationId, venue.id);
  const [showAll, setShowAll] = useState(false);
  const latest = test.data;
  const never = test.isError && problemCode(test.error) === PROBLEM.notFound;
  const failing = latest?.checks.filter((check) => check.outcome === 'fail').length ?? 0;
  const held = latest?.checks.filter((check) => check.outcome === 'pass').length ?? 0;

  let summary: ReactNode;
  if (never || latest === undefined) {
    summary = <p className="console-muted">{t('floor.drawer.lockdownTest.never')}</p>;
  } else if (latest.status === 'pending') {
    summary = (
      <p className="console-muted" role="status">
        {t('floor.drawer.lockdownTest.pending')}
      </p>
    );
  } else if (latest.status === 'expired') {
    summary = <Notice tone="warn" title={t('floor.drawer.lockdownTest.expired')} />;
  } else {
    summary = (
      <p role="status">
        <Badge tone={latest.status === 'passed' ? 'ok' : 'danger'}>
          {latest.status === 'passed' ? t('floor.drawer.lockdownTest.passed', { n: held }) : t('floor.drawer.lockdownTest.failing', { n: failing })}
        </Badge>{' '}
        <span className="console-muted">
          {t('floor.drawer.lockdownTest.ranAt', {
            time: new Date(latest.ranAt ?? latest.requestedAt).toLocaleString(),
            mode: latest.mode === null ? '—' : t(`floor.stationMode.${latest.mode}`),
          })}
        </span>
      </p>
    );
  }

  const shown = latest === undefined || latest.status === 'pending' || latest.status === 'expired' ? [] : orderedChecks(latest).filter((check) => showAll || check.outcome === 'fail');

  return (
    <Section title={t('floor.drawer.lockdownTest.title')} description={t('floor.drawer.lockdownTest.hint')} titleAs="h3">
      {summary}
      {shown.length > 0 ? (
        <ul className="console-health__alerts" aria-label={t('floor.drawer.lockdownTest.title')}>
          {shown.map((check) => (
            <li
              key={check.id}
              className={cx(
                'console-health__alert',
                check.outcome === 'fail' && 'console-health__alert--critical',
                check.outcome === 'pass' && 'console-health__alert--cleared',
              )}
            >
              <Icon name={check.outcome === 'fail' ? 'alert' : 'check'} />
              <span>
                <strong>{check.name}</strong> <span className="console-muted">· {t(`floor.drawer.lockdownTest.outcome.${check.outcome}`)}</span>
                <br />
                <span className="console-muted">{check.detail}</span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {run.isError ? <Notice tone="danger" role="alert" title={problem.text(run.error, { fallback: t('floor.drawer.lockdownTest.sendFailed') })} /> : null}
      <div className="console-form__actions">
        {latest !== undefined && latest.checks.length > 0 ? (
          <Button variant="ghost" size="sm" onClick={() => setShowAll((value) => !value)}>
            {t('floor.drawer.lockdownTest.allChecks')}
          </Button>
        ) : null}
        {canRun ? (
          <Button variant="secondary" icon={<Icon name="lock" />} busy={run.isPending || latest?.status === 'pending'} onClick={() => run.mutate(stationId)}>
            {latest === undefined ? t('floor.drawer.lockdownTest.run') : t('floor.drawer.lockdownTest.again')}
          </Button>
        ) : null}
      </div>
    </Section>
  );
}

/** `actions` sits in the section's header: the floor sheet's close button when it opens from the action bar. */
export function RecoveryCodeSection({ stationId, actions }: { readonly stationId: string; readonly actions?: ReactNode }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const venue = useCurrentVenue();
  const issue = useIssueRecoveryCode(venue.organizationId, venue.id);
  const [code, setCode] = useState<StationRecoveryCode | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const mint = () => {
    setError(undefined);
    setCode(null);
    issue.mutate(stationId, {
      onSuccess: setCode,
      onError: (failure) => setError(problemCode(failure) === PROBLEM.conflict ? t('floor.drawer.recoveryCode.notPaired') : problem.text(failure, { fallback: t('floor.drawer.recoveryCode.failed') })),
    });
  };

  return (
    <Section title={t('floor.drawer.recoveryCode.title')} description={t('floor.drawer.recoveryCode.hint')} titleAs="h3" actions={actions}>
      {code === null ? (
        <div className="console-form__actions">
          <Button variant="secondary" icon={<Icon name="lock" />} busy={issue.isPending} onClick={mint}>
            {t('floor.drawer.recoveryCode.issue')}
          </Button>
        </div>
      ) : (
        <div className="console-station-sheet__code">
          <Notice tone="warn" title={t('floor.drawer.recoveryCode.warning')} />
          <span className="console-muted">{t('floor.drawer.recoveryCode.command')}</span>
          <code>{undoCommand(code.code)}</code>
          <span className="console-muted">{t('floor.drawer.recoveryCode.expires', { time: new Date(code.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })}</span>
          <div className="console-station-sheet__row">
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                void navigator.clipboard?.writeText(undoCommand(code.code)).then(
                  () => setCopied(true),
                  () => undefined,
                )
              }
            >
              {t('floor.drawer.recoveryCode.copy')}
            </Button>
            <span role="status" className="console-muted">
              {copied ? t('floor.drawer.recoveryCode.copied') : ''}
            </span>
          </div>
        </div>
      )}
      {error === undefined ? null : <Notice tone="danger" role="alert" title={error} />}
    </Section>
  );
}
